import { Box, Flex, Text, Image } from "rebass/styled-components";
import useTranslation from "next-translate/useTranslation";

import { useRef, useState } from "react";
import { CurrentUser } from "../../interfaces";
import Button from "../common/Button";
import Modal from "../common/Modal";

import ReactCrop, { Crop } from "react-image-crop";
import { useIsMobile } from "../../lib/media";
import getCroppedImg from "../../lib/crop-image";
import { getImageUrl } from "../../lib/image-url";
import { useToasts } from "react-toast-notifications";

interface Props {
  currentUser: CurrentUser;
  avatar: string;
  onChange: (event) => void;
}

interface CropImageProps {
  src: string;
  onClose: () => void;
  onSave: (Image, Crop) => void;
}

interface ModalFooterProps {
  onClose: () => void;
  onSave: () => void;
}

const ModalFooter = ({ onClose, onSave }: ModalFooterProps) => {
  const { t } = useTranslation("common");

  return (
    <Flex
      pt={1}
      px={[1, 1, 1]}
      py={[1, 1, 0]}
      sx={{
        textAlign: "right",
        justifyContent: "space-between",
        alignItems: "center"
      }}
    >
      <Text
        as="p"
        variant="link"
        sx={{
          color: "springGreen",
          fontSize: 3,
          lineHeight: "body",
          cursor: "pointer",
          display: "inline"
        }}
        onClick={onClose}
      >
        {t("cancel")}
      </Text>
      <Button variant="secondary" onClick={onSave}>
        {t("save")}
      </Button>
    </Flex>
  );
};

function CropImage({ src, onClose, onSave }: CropImageProps) {
  const isMobileViewport = useIsMobile();

  const [state, setState] = useState({
    crop: {
      unit: "%",
      aspect: 1,
      width: 100,
      height: 100,
      x: 0,
      y: 0
    } as Crop,
    imageWidth: "100%",
    imageHeight: "100%",
    image: null
  });

  const { crop, imageWidth, imageHeight, image } = state;
  const save = () => {
    onSave(image, crop);
  };

  const onImageLoaded = image => {
    const width =
      image.width > image.height ? (image.height / image.width) * 100 : 100;
    const height =
      image.height > image.width ? (image.width / image.height) * 100 : 100;
    const x = width === 100 ? 0 : (100 - width) / 2;
    const y = height === 100 ? 0 : (100 - height) / 2;

    setState({
      ...state,
      imageHeight: `${image.height}px`,
      imageWidth: `${image.width}px`,
      crop: {
        unit: "px",
        aspect: 1,
        width: (image.width / 100) * width,
        height: (image.height / 100) * height,
        x: (image.width / 100) * x,
        y: (image.height / 100) * y
      },
      image: image
    });

    return false;
  };

  const onChange = newCrop => {
    setState({
      ...state,
      crop: newCrop
    });
  };

  return isMobileViewport ? (
    <Modal
      open={true}
      size="full"
      FooterButton={<ModalFooter onSave={save} onClose={onClose} />}
      HeaderButton={<Box></Box>}
      onClose={onClose}
    >
      <Box
        mx={"auto"}
        my={2}
        sx={{
          textAlign: "center"
        }}
      >
        <ReactCrop
          src={src}
          crop={crop}
          circularCrop
          onImageLoaded={onImageLoaded}
          onChange={onChange}
        />
      </Box>
    </Modal>
  ) : (
    <Modal
      open={true}
      size="small"
      FooterButton={<ModalFooter onSave={save} onClose={onClose} />}
      HeaderButton={<Box></Box>}
      onOutsideClick={onClose}
      onClose={onClose}
      FooterHeight="80px"
    >
      <Box
        my={2}
        mx={"auto"}
        sx={{
          width: imageWidth,
          height: `calc(${imageHeight} + 80px)`
        }}
      >
        <ReactCrop
          src={src}
          crop={crop}
          circularCrop
          onImageLoaded={onImageLoaded}
          onChange={onChange}
        />
      </Box>
    </Modal>
  );
}

function ProfilePictureUploader({ currentUser, avatar, onChange }: Props) {
  const { t } = useTranslation("common");
  const { addToast } = useToasts();
  const fileUploaderRef = useRef(null);
  const [state, setState] = useState({
    show: false,
    src: null
  });

  const previewImage = event => {
    const acceptedFileTypes = [
      "image/jpg",
      "image/jpeg",
      "image/gif",
      "image/png"
    ];
    const files = Array.prototype.filter.call(event.target.files, file => {
      if (acceptedFileTypes.includes(file.type)) {
        return true;
      } else {
        addToast(
          t("invalidFileType", {
            acceptedTypes: acceptedFileTypes.join(", "),
            invalidType: file.type
          }),
          {
            appearance: "info",
            autoDismiss: true,
            style: {
              maxWidth: "90vw"
            }
          }
        );
        return false;
      }
    });
    event.target.value = "";

    if (files.length > 0) {
      getImageUrl(files[0], undefined).then(value => {
        setState({
          show: true,
          src: value
        });
      });
    }
  };

  const openFileUploader = () => {
    fileUploaderRef.current.multiple = false;
    fileUploaderRef.current.accept = "image/*";
    fileUploaderRef.current.click();
  };

  const onClose = () => {
    setState({
      src: null,
      show: false
    });
  };

  const onSave = (image, crop: Crop) => {
    onChange(getCroppedImg(image, crop));
    setState({
      src: null,
      show: false
    });
  };

  return (
    <Box my={1}>
      <Flex
        sx={{
          flexDirection: "column",
          alignItems: "center"
        }}
      >
        {/* <Label mb={2}>{t("profilePicture")}</Label> */}
        {(avatar || currentUser.avatar) && (
          <Image
            src={avatar || currentUser.avatar.circle}
            alt={`${currentUser.name} avatar`}
            variant="avatar"
            height="96px"
            width="96px"
            mb={2}
          ></Image>
        )}
        <Box width={1}>
          <Button width={1} variant="terciary" onClick={openFileUploader}>
            {!avatar && !currentUser.avatar
              ? t("selectImage")
              : t("changePhoto")}
          </Button>
        </Box>

        <Box
          ref={fileUploaderRef}
          as="input"
          type="file"
          sx={{
            display: "none"
          }}
          onChange={previewImage}
        ></Box>
        {state.show && (
          <CropImage
            src={state.src}
            onSave={onSave}
            onClose={onClose}
          ></CropImage>
        )}
      </Flex>
    </Box>
  );
}

export default ProfilePictureUploader;
